import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Check, Plus } from "lucide-react";
import { EASE, Reveal, Eyebrow, CTAButton } from "./Shared";

const INCLUDED = [
  "The complete BLUEPRINT course",
  "All BLUEPRINT lessons + masterclasses",
  "BONUS: the BECOMING HER Blueprint™ book by Dasha Love",
  "Access to Dasha Love’s Skool community",
  "Instant digital access — nothing shipped, nothing to wait for",
];

const MODULES = [
  ["The Belief Audit", ["Shifting Beliefs about money & freedom", "Reprogram Your Relationship", "Change your mind", "Releasing Triggers"]],
  ["Identity Design", ["Two identities", "Becoming", "Identity embodiment group session"]],
  ["Why Before Money", ["FOCUS Why — not the Money?", "Your Why Identity Masterclass"]],
  ["Desire + Receiving", ["Feeling", "Desire", "Receive", "Creativity"]],
  ["Money Codes", ["money matters", "Money Mantra part 1", "Example: RICH GODDESS mantra"]],
  ["Message + Market", ["Message", "medium-market-message", "Blueprint — Lisa", "What if…", "blueprint"]],
  ["Embodied Action", ["Definition of success", "small wins", "intentional", "Book Session Sample"]],
];

export default function Inside() {
  const [open, setOpen] = useState(0);

  return (
    <section data-testid="inside-section" className="relative py-20 sm:py-32">
      <div className="max-w-6xl mx-auto px-4 sm:px-8">
        <Reveal>
          <Eyebrow>Everything included</Eyebrow>
        </Reveal>
        <Reveal delay={0.05}>
          <h2 className="mt-5 font-display font-extrabold uppercase tracking-tight leading-[0.95] text-3xl sm:text-5xl max-w-3xl">
            One payment.{" "}
            <span className="font-serif italic normal-case font-medium text-lime">The entire blueprint.</span>
          </h2>
        </Reveal>

        <div className="mt-12 sm:mt-16 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <ul className="border-t border-cream/10">
              {INCLUDED.map((item, i) => (
                <Reveal key={item} delay={i * 0.05} y={14}>
                  <li
                    data-testid={`included-item-${i + 1}`}
                    className="flex items-start gap-4 py-4 sm:py-5 border-b border-cream/10"
                  >
                    <span className="mt-0.5 w-6 h-6 shrink-0 bg-lime text-ink flex items-center justify-center">
                      <Check size={14} strokeWidth={3} />
                    </span>
                    <span className="text-sm sm:text-lg text-cream/90 leading-snug">{item}</span>
                  </li>
                </Reveal>
              ))}
            </ul>
          </div>

          <Reveal delay={0.15} className="lg:col-span-5">
            <div className="border border-lime/40 bg-coal p-7 sm:p-9 relative overflow-hidden">
              <span className="absolute top-0 right-0 bg-lime text-ink font-mono text-[9px] font-semibold tracking-[0.15em] uppercase px-3 py-1.5">
                Launch price
              </span>
              <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-fog">Blueprint™</p>
              <p className="mt-4 font-display font-extrabold text-6xl sm:text-7xl leading-none">$111</p>
              <p className="mt-3 text-sm text-fog leading-relaxed">
                Course + masterclasses + Skool community + the bonus BECOMING
                HER Blueprint™ book. One payment, instant access.
              </p>
              <div className="mt-7">
                <CTAButton testId="inside-cta-button" className="w-full">
                  Get instant access — $111
                </CTAButton>
              </div>
              <p className="mt-4 font-mono text-[10px] tracking-[0.2em] uppercase text-fog text-center">
                One payment · Instant digital access
              </p>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.05}>
          <p className="mt-16 sm:mt-20 mb-6 font-mono text-[10px] tracking-[0.3em] uppercase text-fog">
            Inside the course
          </p>
        </Reveal>
        <div className="border-t border-cream/10">
          {MODULES.map(([title, lessons], i) => (
            <div
              key={title}
              data-testid={`curriculum-accordion-item-${i + 1}`}
              className="border-b border-cream/10"
            >
              <button
                data-testid={`curriculum-toggle-${i + 1}`}
                onClick={() => setOpen(open === i ? -1 : i)}
                className="w-full flex items-center justify-between gap-4 py-5 sm:py-6 text-left group"
              >
                <span className="flex items-baseline gap-4 sm:gap-6">
                  <span className="font-mono text-[10px] sm:text-xs text-lime">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="font-display font-bold uppercase tracking-tight text-lg sm:text-2xl group-hover:text-lime transition-colors duration-300">
                    {title}
                  </span>
                </span>
                <Plus
                  size={20}
                  className={`shrink-0 text-lime transition-transform duration-300 ${open === i ? "rotate-45" : ""}`}
                />
              </button>
              <AnimatePresence initial={false}>
                {open === i && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.4, ease: EASE }}
                    className="overflow-hidden"
                  >
                    <ul className="pb-6 pl-8 sm:pl-12 space-y-2 max-w-2xl">
                      {lessons.map((lesson) => (
                        <li key={lesson} className="flex items-baseline gap-3 text-sm sm:text-base text-fog">
                          <span className="text-lime shrink-0">—</span>
                          <span>{lesson}</span>
                        </li>
                      ))}
                    </ul>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </div>
        <Reveal delay={0.05}>
          <p className="mt-8 font-mono text-[10px] tracking-[0.2em] uppercase text-fog leading-relaxed">
            Plus guided reflection + experimentation practices inside — pineal gland (3rd eye)
            meditation · the &ldquo;Quantum?&rdquo; exploration
          </p>
        </Reveal>
      </div>
    </section>
  );
}
